import type {AxiosError} from 'axios';
import type {IBookingMachineContext} from './bookingMachine.types';

import {AnalyticsService} from 'src/analytics';
import {getPlatform} from '../../utils/platform';
import {formatISO} from '../../utils/date';
import {ANALYTICS_SCHEDULER_EVENTS} from '../../analytics/analytics-event-names';
import {getDurationInMinutes} from '../../utils/analytics';
import {shouldSkipSequentialAnalytics} from '../../analytics/analytics-events';
import type {IEvent} from '../../types/IEvent';


export function trackBookingMeetingLinkVisited(
  context: IBookingMachineContext,
): void {
  AnalyticsService.track(ANALYTICS_SCHEDULER_EVENTS.MEETING_LINK_VISITED, {
    template_type: context.eventTemplate?.distribution_rule,
    link_source: context.session?.id ? 'session_link' : 'static_link',
    platform: getPlatform(),
  });
}

export function trackBookingMeetingSlotUnavailable(
  context: IBookingMachineContext,
): void {
  AnalyticsService.track(ANALYTICS_SCHEDULER_EVENTS.MEETING_SLOT_UNAVAILABLE, {
    template_type: context.eventTemplate?.distribution_rule,
    platform: getPlatform(),
  });
}

export function trackBookingNotifyHostSubmitted(
  context: IBookingMachineContext,
): void {
  AnalyticsService.track(ANALYTICS_SCHEDULER_EVENTS.NOTIFY_HOST_SUBMITTED, {
    template_type: context.eventTemplate?.distribution_rule,
  });
}

export function trackBookingConfirmed(context: IBookingMachineContext): void {
  if (shouldSkipSequentialAnalytics(context)) {
    return;
  }

  const event = context.eventDetails as IEvent;
  AnalyticsService.track(ANALYTICS_SCHEDULER_EVENTS.BOOKING_CONFIRMED, {
    template_type: context.eventTemplate?.distribution_rule,
    candidate_name: event?.primary_guest_name,
    candidate_email: event?.primary_guest_email,
    candidate_phone: event?.primary_guest_phone,
    candidate_timezone: event?.primary_guest_timezone,
    candidate_additional_question_answers:
      event?.primary_guest_additional_question_answers,
    meeting_date: context.sequenceDays?.[0]?.selectedDate,
    meeting_time: context.sequenceDays?.[0]?.selectedTimeSlot?.start_time,
    duration_in_minutes: getDurationInMinutes(
      context.eventTemplate?.duration ?? {amount: 0, unit: 'minutes'},
    ),
  });
}

export function trackBookingRescheduleConfirmed(
  context: IBookingMachineContext,
): void {
  if (shouldSkipSequentialAnalytics(context)) {
    return;
  }
  const event = context.eventDetails as IEvent;
  AnalyticsService.track(ANALYTICS_SCHEDULER_EVENTS.RESCHEDULE_CONFIRMED, {
    reschedule_resource: context.rescheduleSource ?? 'confirmation_email',
    template_type: context.eventTemplate?.distribution_rule,
    original_meeting_date: formatISO(event?.event_start ?? new Date(), {
      representation: 'date',
    }),
    new_meeting_date: context.sequenceDays?.[0]?.selectedDate,
    new_meeting_time: context.sequenceDays?.[0]?.selectedTimeSlot?.start_time,
  });
}

export function trackBookingError(context: IBookingMachineContext): void {
  const error = context.error;
  const axiosError = error as AxiosError<{errors?: string[]}> | undefined;
  const errorCode = axiosError?.response?.data?.errors?.[0];
  const errorMessage =
    axiosError?.message ?? (error !== undefined ? String(error) : undefined);

  AnalyticsService.track(ANALYTICS_SCHEDULER_EVENTS.BOOKING_ERROR, {
    template_type: context.eventTemplate?.distribution_rule,
    error_message: errorMessage,
    error_code: errorCode,
  });
}

export const bookingMachineAnalyticsActions = {
  trackMeetingLinkVisited: ({context}: {context: IBookingMachineContext}) =>
    trackBookingMeetingLinkVisited(context),
  trackMeetingSlotUnavailable: ({context}: {context: IBookingMachineContext}) =>
    trackBookingMeetingSlotUnavailable(context),
  trackNotifyHostSubmitted: ({context}: {context: IBookingMachineContext}) =>
    trackBookingNotifyHostSubmitted(context),
  trackBookingConfirmed: ({context}: {context: IBookingMachineContext}) =>
    trackBookingConfirmed(context),
  trackBookingError: ({context}: {context: IBookingMachineContext}) =>
    trackBookingError(context),
  trackBookingRescheduleConfirmed: ({
    context,
  }: {
    context: IBookingMachineContext;
  }) => trackBookingRescheduleConfirmed(context),
};
